import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { filterTableMutations } from "@/react/components/table/filterTableMutations";
import TableComponent from "@/react/components/table/TableComponent";
import { useTable } from "@/react/components/table/useTable";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import TableWrapper from "@/react/features/styled/TableWrapper";
import { useErrorNotification } from "@/react/notifications";

import useBackgroundQuery from "./useBackgroundQuery";

/**
 * This is a wrapped `TableComponent` for easily rendering the table associated with the `useDataTable` hook.
 *
 * @param props The properties to pass to the table component rendering this data table
 * @return {JSX.Element} The data table element
 * @constructor
 */
export const DataTable = (props) => {
  return (
    <TableWrapper>
      <TableComponent {...props} />
    </TableWrapper>
  );
};

/**
 * Convenience hook for creating a table component that handles data from an API query,
 * displays that data in the table, and notifies in the case of an error. The table:
 * - automatically refetches data upon changes in the customer ID
 * - provides default implementations for:
 *   - showing fetch errors,
 *   - determining if the table is dirty
 *   - determining if the table is in the midst of running the API query
 *   - the Refresh button
 *   - the Cancel button
 * - enables background query handling
 *
 * These items can be modified by the custom implementations provided in the hook arguments.
 * Internally, this uses the `useTable` hook.
 *
 * @param title The title of the table (not displayed in the UI)
 * @param queryHookResult The result object returned from calling the query hook that fetches the data from the API
 * @param schema An object representing the schema of the data retrieved by the API and displayed in the table, conforming to the signature below.
 * The `key` is necessary if the table is to have modifying mutations (i.e. not read-only).
 * <pre>
 * {
 *  definition: ObjectSchema, // The Yup schema object
 *  key: string // [Optional] The key to use as the unique identifier for the data in the table
 * }
 * </pre>
 * @param columns The column definitions for displaying the data in the table
 * @param mutationsFilter The filter object to select which table mutations are allowed (if not provided, table is read-only); passed directly to `filterTableMutations`
 * @param tableOptions Optional - Additional options for creating the table, provided to the `useTable` hook
 * @param isDirtyFn Optional - Function to determine if the table is dirty, taking as arguments the rows to create, update, and delete (as provided by the mutations object)
 * @param isLoadingFn Optional - Function to determine if the table is in the midst of an operation, taking as argument the isFetching flag (as provided by the query hook).
 * Note that the background query should *not* be considered for purposes of this loading indicator. This only applies to the primary query.
 * @param backgroundQuery Optional - An object containing the information necessary to run a background query to update the table data.
 * This background operation is executed after every run of the primary query, unless specified otherwise. The object has the following properties:
 * <pre>
 * {
 *   trigger: Function, // The function to call to run the background query
 *   queryHookResults: Object, // The object returned by the query hook for the background query
 *   shouldRunQuery: Function, // Function to determine if the background query should be run, taking as argument the mutations object. If not provided, the background query is always run (assuming one is provided)
 *   queryArg: any, // The argument to pass to the background query function
 *   responseHandler: Function // Optional function to handle the response from the background query. The setter to call to replace the table data is provided as the second argument. If no function is provided, the results from the background query simply replace the data in the table.
 * }
 * </pre>
 * @return The objects and component properties associated with the table. The primary items are the data itself, the DataTable props, and the error object.
 * Also included are all the items returned from the query hook and the `useTable` hook. The available objects are:
 * <pre>
 * {
 *   data, // The data displayed in the table
 *   props, // The properties to pass to the table component for rendering this data table. It should be used as `<DataTable {...props}/>`. Note that there is no `onSave` callback provided, as this is expected to be supplied directly: `<DataTable {...props} onSave={saveFn}/>`
 *   error, // Possible error from the API query
 *   isFetching, // Flag indicating if the API query is in progress
 *   refetch, // Function to manually refetch the data (rerun the API query)
 *   table, // The `table` object returned by the `useTable` hook
 *   filters, // The `filters` object returned by the `useTable` hook
 *   state, // The `state` object returned by the `useTable` hook
 *   mutations, // The `mutations` object returned by the `useTable` hook
 *   finalMutations, // The mutations actually used for the table (after applying the `mutationsFilter`)
 *   isDirty, // Flag indicating if the table is dirty (has unsaved changes)
 *   isLoading, // Flag indicating if the table is in the midst of an operation
 *   isBackgroundLoading, // Flag indicating if the background query is in progress
 *   backgroundError, // Possible error from the background query
 *   queryHookResult, // The object returned by the query hook; same as passed in as an argument
 *   tableResult // The object returned by the `useTable` hook
 *   backgroundQueryHookResults // The object returned by the background query hook
 * }
 * </pre>
 */
export const useDataTable = ({
  title,
  queryHookResult,
  schema,
  columns,
  mutationsFilter,
  tableOptions,
  isDirtyFn,
  isLoadingFn,
  backgroundQuery,
}) => {
  const customerId = useSelector(customerIdSelector);
  const [tableData, setTableData] = useState(null);

  const { data: queryData, isFetching, error, refetch } = queryHookResult;

  // This is so that we auto-reload the table when the customer changes
  useEffect(() => {
    refetch && refetch();
  }, [customerId]);

  // If the query data changes, then reset the table with that new data
  // This would imply a new result or a different query
  useEffect(() => {
    setTableData(queryData);
  }, [queryData]);

  useErrorNotification(error);

  const tableResult = useTable(
    tableData,
    columns,
    schema.definition,
    schema?.key,
    tableOptions
  );
  const { table, filters, state, mutations } = tableResult;

  const { data, rowsToCreate, rowsToUpdate, rowsToDelete, resetData } =
    mutations;
  const finalMutations = useMemo(
    () => filterTableMutations(mutations, mutationsFilter),
    [mutations]
  );

  const isDirty = useMemo(
    () =>
      isDirtyFn
        ? isDirtyFn(rowsToCreate, rowsToUpdate, rowsToDelete)
        : rowsToCreate.size > 0 ||
          rowsToUpdate.size > 0 ||
          rowsToDelete.size > 0,
    [isDirtyFn, rowsToCreate, rowsToUpdate, rowsToDelete]
  );

  const isLoading = useMemo(
    () => (isLoadingFn ? isLoadingFn(isFetching) : isFetching),
    [isLoadingFn, isFetching]
  );

  const isEmpty = useMemo(() => {
    // Don't consider the background data, it could be stale,
    // because currently we don't reset it when we don't need to run the query
    return queryData?.length === 0 && data?.length === 0;
  }, [queryData, data]);

  const {
    isBackgroundLoading,
    abortBackgroundQuery,
    backgroundError,
    backgroundQueryHookResults,
  } = useBackgroundQuery({
    backgroundQuery,
    title,
    queryData,
    data,
    isLoading,
    setTableData,
  });

  const isInactive = useMemo(
    () => !isLoading && !isBackgroundLoading,
    [isLoading, isBackgroundLoading]
  );

  const props = useMemo(() => {
    return {
      title,
      schema: schema.definition,
      isDirty,
      isLoading,
      onCancel: resetData,
      onRefresh: refetch,
      onAbort: abortBackgroundQuery,
      table,
      filters,
      mutations: finalMutations,
      state,
      isPartialData: isBackgroundLoading,
    };
  }, [
    title,
    schema,
    isDirty,
    isLoading,
    resetData,
    refetch,
    abortBackgroundQuery,
    table,
    filters,
    finalMutations,
    state,
    isBackgroundLoading,
  ]);

  return {
    // Primary objects
    data,
    props,
    error,
    // Secondary objects
    isFetching,
    refetch,
    table,
    filters,
    state,
    mutations,
    finalMutations,
    isDirty,
    isLoading,
    isBackgroundLoading,
    backgroundError,
    queryHookResult,
    tableResult,
    backgroundQueryHookResults,
    isEmpty,
    isInactive,
  };
};
